import { AutocompleteSource, BaseItem } from "@algolia/autocomplete-core";

import {
    RECENT_SEARCHES_COUNT,
    RECENT_SEARCHES_SOURCE_ID,
} from "@components/Search/SearchAutocomplete/autoCompleteConstants";
import { RecentSearchesSourceType } from "@components/Search/SearchAutocomplete/autocompleteTypes";

import { getRedirectUrlFromState } from "../helpers/redirect.helpers";

export type RecentSearch = BaseItem & {
    label: string;
    redirectUrl?: string;
};

const getRecentSearches = (): RecentSearch[] => {
    const localStorageRecentSearches = localStorage.getItem("recent_searches");

    if (!localStorageRecentSearches) return [];

    return (
        (JSON.parse(localStorageRecentSearches) as (RecentSearch | string)[])
            // Map legacy records to new model
            .map(recentSearch =>
                typeof recentSearch === "string"
                    ? { label: recentSearch }
                    : recentSearch,
            )
    );
};

const saveRecentSearches = (items: RecentSearch[]) => {
    localStorage.setItem(
        "recent_searches",
        JSON.stringify([
            ...items.map(item => ({
                label: item.label,
                redirectUrl: item.redirectUrl,
            })),
        ]),
    );
};

export const clearRecentSearches = () => {
    localStorage.removeItem("recent_searches");
};

export const addRecentSearch = (recentSearch: RecentSearch) => {
    if (recentSearch.label.length === 0) return;

    const recentSearches = getRecentSearches()
        .filter(({ label }) => label !== recentSearch.label)
        .slice(0, RECENT_SEARCHES_COUNT - 1);
    recentSearches.unshift(recentSearch);

    saveRecentSearches(recentSearches);
};

export const removeRecentSearchesItem = (label: string) => {
    const recentSearches = getRecentSearches().filter(
        recentSearchesItem => recentSearchesItem.label !== label,
    );

    saveRecentSearches(recentSearches);
};

export const hasRecentSearches = () => getRecentSearches().length > 0;

export const recentSearchesSource: AutocompleteSource<RecentSearchesSourceType> =
    {
        sourceId: RECENT_SEARCHES_SOURCE_ID,
        getItemInputValue({ item }) {
            return item.label;
        },
        onSelect({ state }) {
            const redirectUrl = getRedirectUrlFromState(state);

            addRecentSearch({
                label: state.query,
                redirectUrl: redirectUrl,
            });
        },
        getItems() {
            return getRecentSearches().map(recentSearch => ({
                ...recentSearch,
                __autocomplete_id: null,
                __autocomplete_queryID: undefined,
            }));
        },
    };
